/**
 * Centralized Browser Storage Utilities
 *
 * Provides type-safe localStorage and sessionStorage operations with:
 * - JSON serialization/deserialization
 * - Silent error handling for private browsing
 * - Optional TTL (time-to-live) expiration
 * - Validation support
 */

const STORAGE_PREFIX = 'billbee-';

interface StoredItem<T> {
  value: T;
  timestamp: number;
  expiresAt?: number;
}

/**
 * Check if a stored item has expired
 */
function isExpired<T>(item: StoredItem<T>): boolean {
  if (!item.expiresAt) return false;
  return Date.now() > item.expiresAt;
}

/**
 * Get an item from storage with type safety and expiration handling
 */
function getItem<T>(
  storage: Storage,
  key: string,
  validator?: (value: unknown) => value is T
): T | null {
  try {
    const raw = storage.getItem(`${STORAGE_PREFIX}${key}`);
    if (!raw) return null;

    const stored = JSON.parse(raw) as StoredItem<T>;

    // Handle legacy items stored without wrapper
    if (!('value' in stored) || !('timestamp' in stored)) {
      // Assume it's a legacy format, return as-is if valid
      const legacyValue = stored as unknown;
      if (validator && !validator(legacyValue)) return null;
      return legacyValue as T;
    }

    // Check expiration
    if (isExpired(stored)) {
      storage.removeItem(`${STORAGE_PREFIX}${key}`);
      return null;
    }

    // Validate if validator provided
    if (validator && !validator(stored.value)) {
      return null;
    }

    return stored.value;
  } catch {
    return null;
  }
}

/**
 * Set an item in storage with optional TTL
 */
function setItem<T>(storage: Storage, key: string, value: T, ttlMs?: number): boolean {
  try {
    const item: StoredItem<T> = {
      value,
      timestamp: Date.now(),
      expiresAt: ttlMs ? Date.now() + ttlMs : undefined,
    };
    storage.setItem(`${STORAGE_PREFIX}${key}`, JSON.stringify(item));
    return true;
  } catch {
    // Silent fail for private browsing or quota exceeded
    return false;
  }
}

/**
 * Remove an item from storage
 */
function removeItem(storage: Storage, key: string): boolean {
  try {
    storage.removeItem(`${STORAGE_PREFIX}${key}`);
    return true;
  } catch {
    return false;
  }
}

/**
 * localStorage operations with billbee- prefix
 */
export const local = {
  /**
   * Get an item from localStorage
   * @param key - Storage key (without prefix)
   * @param validator - Optional type guard for validation
   */
  get<T>(key: string, validator?: (value: unknown) => value is T): T | null {
    if (typeof window === 'undefined') return null;
    return getItem<T>(localStorage, key, validator);
  },

  /**
   * Set an item in localStorage
   * @param key - Storage key (without prefix)
   * @param value - Value to store
   * @param ttlMs - Optional time-to-live in milliseconds
   */
  set<T>(key: string, value: T, ttlMs?: number): boolean {
    if (typeof window === 'undefined') return false;
    return setItem(localStorage, key, value, ttlMs);
  },

  /**
   * Remove an item from localStorage
   */
  remove(key: string): boolean {
    if (typeof window === 'undefined') return false;
    return removeItem(localStorage, key);
  },
};

/**
 * sessionStorage operations with billbee- prefix
 */
export const session = {
  /**
   * Get an item from sessionStorage
   * @param key - Storage key (without prefix)
   * @param validator - Optional type guard for validation
   */
  get<T>(key: string, validator?: (value: unknown) => value is T): T | null {
    if (typeof window === 'undefined') return null;
    return getItem<T>(sessionStorage, key, validator);
  },

  /**
   * Set an item in sessionStorage
   * @param key - Storage key (without prefix)
   * @param value - Value to store
   * @param ttlMs - Optional time-to-live in milliseconds
   */
  set<T>(key: string, value: T, ttlMs?: number): boolean {
    if (typeof window === 'undefined') return false;
    return setItem(sessionStorage, key, value, ttlMs);
  },

  /**
   * Remove an item from sessionStorage
   */
  remove(key: string): boolean {
    if (typeof window === 'undefined') return false;
    return removeItem(sessionStorage, key);
  },
};
